import React from 'react';
import { motion } from 'framer-motion';
import { HiAcademicCap, HiCode, HiUserGroup, HiLightBulb } from 'react-icons/hi';

const About = () => {
  const highlights = [
    {
      icon: HiCode,
      title: '500+ PRs',
      description: 'Production Contributions',
    },
    {
      icon: HiLightBulb,
      title: 'Problem Solver',
      description: 'Scalable Solutions',
    },
    {
      icon: HiUserGroup,
      title: 'Mentor & Lead',
      description: 'Teaching At Scale',
    },
    {
      icon: HiAcademicCap,
      title: 'Multilingual',
      description: 'English, Persian, Pashto',
    },
  ];

  return (
    <section id="about" className="py-12 sm:py-16 md:py-20 bg-beige-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 relative z-10">
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-beige-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">Get to know me</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-900 mt-2 font-primary">
            About Me
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-beige-500 mx-auto mt-3 sm:mt-4"></div>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
              I'm <span className="font-bold text-navy-800">Wajiha Niazi</span>, a{' '}
              <span className="font-semibold text-navy-700">full-stack software engineer and technical mentor</span>{' '}
              building scalable, user-focused products for startups and impact-driven organizations.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
              My core stack includes{' '}
              <span className="font-semibold text-navy-700">
                React, Next.js, TypeScript, Node.js, GraphQL, PostgreSQL, and microservices
              </span>
              . Over the past few years, I have contributed 500+ production pull requests across 20+
              projects, balancing shipping speed with clean architecture, maintainability, and product impact.
            </p>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
              Alongside engineering, I lead and mentor through programs at{' '}
              <span className="font-semibold text-navy-700">Code to Inspire</span> and related initiatives,
              where I teach front-end development, design project-based curricula, and help students build
              job-ready portfolios. I care deeply about making tech education more accessible, especially for
              women entering the industry.
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3 mt-6 sm:mt-8">
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-navy-100 text-navy-800 rounded-lg text-xs sm:text-sm font-medium">
                Remote Work
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-beige-200 text-beige-900 rounded-lg text-xs sm:text-sm font-medium">
                Team Leadership
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-navy-100 text-navy-800 rounded-lg text-xs sm:text-sm font-medium">
                Agile/Scrum
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-beige-200 text-beige-900 rounded-lg text-xs sm:text-sm font-medium">
                Mentorship
              </span>
            </div>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {highlights.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-beige-200 hover:border-beige-400 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="text-beige-600 mb-2 sm:mb-3 group-hover:text-beige-700 transition-colors">
                    <IconComponent className="text-2xl sm:text-3xl" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-navy-900 mb-1 font-primary">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        <motion.div
          className="mt-10 sm:mt-12 md:mt-16 grid md:grid-cols-2 gap-6 sm:gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl">
            <HiAcademicCap className="text-3xl sm:text-4xl text-beige-400 mb-3 sm:mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 font-primary">Education</h3>
            <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
              <div>
                <p className="text-beige-300 font-semibold text-sm sm:text-base">Bachelor's in Computer Science</p>
                <p className="text-gray-400 text-xs sm:text-sm">Herat University (2018 - 2021)</p>
              </div>
              <div>
                <p className="text-beige-300 font-semibold text-sm sm:text-base">Full Stack Web Development</p>
                <p className="text-gray-400 text-xs sm:text-sm">Code to Inspire (2018 - 2020)</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-beige-500 to-beige-600 p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl">
            <HiLightBulb className="text-3xl sm:text-4xl text-navy-900 mb-3 sm:mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold text-navy-900 mb-2 font-primary">What Drives Me</h3>
            <p className="text-navy-800 leading-relaxed mt-4 sm:mt-6 text-sm sm:text-base">
              I believe in creating technology that makes a difference. Whether it's building scalable
              applications or mentoring the next generation of developers, I'm committed to excellence
              and empowering others in tech.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
